import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { useIntersectionObserver } from '@trqk/hooks';
import { LeftNavHeader } from '@trqk/components';

import { WebLayout } from '../../templates';
import {
    StatementsChartsPreview,
    StatementsPreviewHeaderContent,
} from './components';
import { sectionMap, sectionIds, defaultSectionConfigs } from './constants';
import { HASHES, MAIN_CONTENT_ID } from '../../../constants';

/**
 * Displays a visualization of the initially uploaded PRO statement as a series of charts. This view
 * is publicly available; anyone can try out Trqk by uploading a statement and then viewing this
 * page.
 */
export const StatementsPreview = (): JSX.Element => {
    const [sectionConfigs, setSectionConfigs] = useState<HeaderSectionConfig[]>(
        defaultSectionConfigs,
    );

    const [showMediaExplorer, setShowMediaExplorer] = useState(true);

    const sectionsToRender = sectionConfigs.reduce(
        (
            acc: HeaderSection[],
            sectionConfig: HeaderSectionConfig,
        ): HeaderSection[] => {
            if (sectionConfig.render) {
                if (sectionConfig.section.id === HASHES.STATEMENTS.EXPLORER) {
                    if (showMediaExplorer) {
                        acc.push(sectionConfig.section);
                    }
                } else {
                    acc.push(sectionConfig.section);
                }
            }

            return acc;
        },
        [] as HeaderSection[],
    );

    const history = useHistory();

    const { activeSectionId, observe } = useIntersectionObserver({
        defaultSectionId:
            sectionsToRender?.[0]?.id ?? sectionMap.earningsAndRanges.id,
        sectionIds,
        observerOptions: {
            root: document.querySelector(MAIN_CONTENT_ID),
            rootMargin: '0px',
            threshold: 0.8,
        },
        onSetActiveSectionId: (id: string) => {
            if (history.location.hash !== id) {
                history.replace({
                    hash: id,
                    search: history.location.search,
                });
            }
        },
    });

    return (
        <WebLayout
            collapsedHeaderPadding="sm"
            headerPadding="lg"
            header={{
                content: (
                    <LeftNavHeader
                        activeSectionId={activeSectionId}
                        sections={sectionsToRender}
                    >
                        <StatementsPreviewHeaderContent displayMode="charts" />
                    </LeftNavHeader>
                ),
                wrapperClass: 'semi-transparent-header',
            }}
        >
            <StatementsChartsPreview
                ref={observe}
                setSectionConfigs={setSectionConfigs}
                setShowMediaExplorer={setShowMediaExplorer}
            />
        </WebLayout>
    );
};
